import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import type { NativeStackHeaderProps } from '@react-navigation/native-stack';
import { Column, Row } from 'app/design/primitives/box';
import {
  createDefaultNativeStackNavigator,
  createModalScreenOptions,
  createWebModalScreenOptions,
} from 'app/lib/navigation';
import { AllListingsScreenLazy } from 'app/screens/billboard/all-listings/lazy';
import { OneChannelScreen } from 'app/screens/chat/one-channel';
import { MyNeighbourhoodScreen } from 'app/screens/profile/my-neighbourhood';
import { GroupedListingScreen } from 'app/screens/shared/grouped-listing';
import type { Address, NewAddressSource } from 'app/types/address';
import type { Category } from 'app/types/category';
import type { Channel, TextMessageMessage } from 'app/types/chat';
import { ListingType, type Listing, type ListingsSortBy } from 'app/types/listing';
import type { Loan } from 'app/types/loan';
import type { SpecificNavigationProp, SpecificRouteProp } from 'app/types/navigation';
import type { FindAllUsersDTO, User } from 'app/types/user';
import { useIsGuestWeb, useUISize } from 'app/utils/web-helpers';
import React, { useContext, useEffect, type ComponentProps } from 'react';
import { View } from 'react-native';
import { OneListingScreen } from '../shared/one-listing';
import { LenderParticipateScreen } from '../shared/one-listing/listing-activity-screen';
import { RatingsScreen } from '../shared/ratings';
import { LandingPageScreen } from './landing-page';
import { NeighbourListingsScreen } from './neighbour-listings';
import { NeighbourProfileScreen } from './neighbour-profile';
import { ProfileSharesScreen } from './neighbour-profile/share-history';
import { TrustedMembersScreen } from './neighbour-profile/trusted-members';
import { LoanDatesPickerScreen } from '../shared/loan-dates-picker';
import { useQueryClient } from '@tanstack/react-query';
import { isLoggedInAtom, isStartingAtom } from 'app/atoms';
import { MobileHomeHeaderLeft } from 'app/components/mobile-home-header-left';
import { MobileHomeHeaderRight } from 'app/components/mobile-home-header-right';
import { SearchInput } from 'app/design/primitives/input/search';
import { useQueryKeys } from 'app/network/invalidation';
import { createTheme as createDSTheme } from 'app/shopify';
import { useAtomValue } from 'jotai';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PresentPartageClubScreen } from '../intro/present-partage-club';
import { IntroScreen } from '../onboarding/intro';
import { MyInformationScreen } from '../profile/my-information';
import { ListingsFilters, useFilterPreferences } from './all-listings/components/listings-filters';
import { AllListingsFilterContext } from './all-listings/use-all-listings-screen';
import { useAuthorize } from 'app/hooks/use-authorize';
import { navigationRef } from 'app/lib/root-navigation';
import type { AuthenticationGateSource } from 'app/atoms';
import { LogoComponent } from 'app/lib/navigation/mobile/logo';
import { GuestMobileListingsFilters } from './all-listings/components/guest-mobile-listings-filters';
import { CatalogSearchHeader } from './catalog-search-header';

const theme = createDSTheme();
const GUEST_MOBILE_HEADER_CONTENT_HEIGHT = 104;

export type Parameters = {
  'landing-page': undefined;
  'all-listings':
    | {
        tagId?: string;
        categoryId?: Category['id'];
        type?: Listing['type'];
        activeTab?: Listing['type'] | 'people';
        userNowContributor?: boolean;
        search?: string;
        searchCenterLatitude?: number;
        searchCenterLongitude?: number;
        searchDistance?: number;
        searchLocationLabel?: string;
        sort?: ListingsSortBy;
      }
    | undefined;
  'neighbour-listings': { type: Listing['type']; userId: User['id'] };
  'neighbour-profile': {
    userId: User['id'];
    listingId?: Listing['id'];
    listingType?: Listing['type'];
    channelURL?: Channel['channel_url'];
  };
  'profile-shares': { userId: User['id']; scope: 'all' | 'common' };
  'trusted-members': { mode: 'circle'; userId?: undefined } | { mode: 'trusted-by'; userId: User['id'] };
  'one-listing': {
    id: Listing['id'];
    newServiceCategoryId?: string;
    newCategoryId?: string;
    newAddress?: Listing['address'];
    fromNewScreen?: boolean;
    hideApproximateLocation?: boolean; // when navigating to one-listing from the map hide approx. location because the map already indicates the approx. location
    searchCenterLatitude?: number;
    searchCenterLongitude?: number;
    searchDistance?: number;
  };
  'listing-activity': {
    listingId: Listing['id'];
    isLended: boolean;
  };
  ratings: {
    userId?: User['id'];
    listingId?: Listing['id'];
    subjectName?: string;
    listingTitle?: string;
    scrollToLoanId?: Loan['id'];
  };
  'grouped-listing': {
    id: Listing['id'];
    searchCenterLatitude?: number;
    searchCenterLongitude?: number;
    searchDistance?: number;
  };
  'group-members': FindAllUsersDTO;
  'one-channel': { url: string; messagePrompt?: TextMessageMessage; toast?: string };
  'loan-dates-picker': {
    url: string;
    promptIntroduceYourself: boolean;
    goBackTo: 'one-channel' | 'one-loan';
    loanId?: Loan['id'];
  };
  'my-neighbourhood': { addressId?: Address['id']; newAddressSource?: NewAddressSource } | undefined;
  presentPartageClub: undefined;
  'my-information': undefined;
  onboardingIntro: { login: boolean } | undefined;
};

export { linking, listingLinking, newAddressLinking, userProfileLinking } from './navigator.linking';

const Stack = createDefaultNativeStackNavigator<Parameters>();

function GuestProtectedRoute({ source }: { source: AuthenticationGateSource }) {
  const authorize = useAuthorize(source);

  useEffect(() => {
    if (navigationRef.isReady()) {
      navigationRef.navigate(
        'main',
        {
          screen: 'billboard',
          params: { screen: 'all-listings', params: { activeTab: ListingType.offer, type: ListingType.offer } },
        },
        { pop: true },
      );
    }
    authorize(() => undefined);
  }, [authorize]);

  return null;
}

function GuestContactRoute() {
  return <GuestProtectedRoute source="contact" />;
}

function GuestReviewsRoute() {
  return <GuestProtectedRoute source="reviews" />;
}

function GuestProfileRoute() {
  return <GuestProtectedRoute source="member-profile" />;
}

function GuestProposeDatesRoute() {
  return <GuestProtectedRoute source="propose-dates" />;
}

function FilterProvider({ children }: { children: React.ReactNode }) {
  const appliedFilters = useFilterPreferences();
  return <AllListingsFilterContext.Provider value={appliedFilters}>{children}</AllListingsFilterContext.Provider>;
}

function Header({ navigation: rawNavigation, route: rawRoute }: NativeStackHeaderProps) {
  const navigation = rawNavigation as unknown as SpecificNavigationProp<'all-listings'>;
  const route = rawRoute as unknown as SpecificRouteProp<'all-listings'>;
  const appliedFilters = useContext(AllListingsFilterContext);
  const qk = useQueryKeys();
  const client = useQueryClient();
  const isGuestWeb = useIsGuestWeb();

  const doSearch = (query: string) => {
    navigation.setParams({ search: query });
    if (route.params?.search) {
      client.setQueryData<User>(qk.selfUser.key, (old) => {
        if (!old) return old;
        if (!old.recentSearches) return old;
        return { ...old, recentSearches: [query, ...old.recentSearches.filter((s) => s !== query)] };
      });
    }
  };

  const searchDistance = appliedFilters?.searchDistanceState[0];

  const handleBackPress = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      navigation.reset({
        index: 0,
        routes: [{ name: 'landing-page' }],
      });
    }
  };

  if (isGuestWeb) {
    return <GuestMobileHeader doSearch={doSearch} navigation={navigation} route={route} />;
  }

  return (
    <CatalogSearchHeader
      onBack={handleBackPress}
      onSearch={doSearch}
      search={route.params?.search ?? ''}
      filters={
        appliedFilters ? (
          <ListingsFilters
            appliedFilters={appliedFilters}
            color={theme.colors['text.primary']}
            defaultDistance={searchDistance}
            listingType={route.params?.type ?? ListingType.offer}
            numFilters={2}
          />
        ) : null
      }
    />
  );
}

function GuestMobileHeader({
  doSearch,
  navigation,
  route,
}: {
  doSearch: (query: string) => void;
  navigation: SpecificNavigationProp<'all-listings'>;
  route: SpecificRouteProp<'all-listings'>;
}) {
  const safeAreaInsets = useSafeAreaInsets();

  return (
    <Column backgroundColor="page" zIndex={3}>
      <Row
        alignItems="center"
        justifyContent="center"
        paddingHorizontal="spacing.general.x4"
        style={{ height: 48 + safeAreaInsets.top, paddingTop: safeAreaInsets.top }}
      >
        <Column
          position="absolute"
          style={{ left: theme.spacing['spacing.general.x4'], top: safeAreaInsets.top + 8 }}
          width={40}
        />
        <LogoComponent height={24} width={112} />
      </Row>
      <Row
        alignItems="center"
        gap="spacing.general.x2"
        paddingHorizontal="spacing.general.x4"
        paddingVertical="spacing.general.x2"
      >
        <Column flex={1}>
          <SearchInput initialValue={route.params?.search ?? ''} onSubmit={doSearch} />
        </Column>
        <GuestMobileListingsFilters navigation={navigation} route={route} />
      </Row>
    </Column>
  );
}

// Stable render wrapper — see navigation.tsx DesktopHeaderRenderer for the same pattern.
// Header must be rendered as a React element (not called as a plain function) so that its
// hooks are tracked under its own fiber, not under HeaderContainer from @react-navigation/stack.
function HeaderRenderer(props: NativeStackHeaderProps) {
  return <Header {...props} />;
}

function MobileHomeHeaderLeftRenderer() {
  return <MobileHomeHeaderLeft />;
}
function MobileHomeHeaderRightRenderer() {
  return <MobileHomeHeaderRight />;
}

function getInitialBillboardRoute(isLoggedIn: boolean, isGuestWeb: boolean, isLargeUI: boolean) {
  if (isLoggedIn) return 'landing-page';
  if (isGuestWeb || isLargeUI) return 'all-listings';
  return 'presentPartageClub';
}

export function Navigator() {
  const { isSmallUI, isLargeUI } = useUISize();
  const safeAreaInsets = useSafeAreaInsets();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isStarting = useAtomValue(isStartingAtom);
  const isGuestWeb = useIsGuestWeb();

  if (isStarting) {
    return null;
  }

  return (
    <FilterProvider>
      <View style={{ flex: 1 }}>
        <Stack.Navigator
          initialRouteName={getInitialBillboardRoute(isLoggedIn, isGuestWeb, isLargeUI)}
          screenOptions={{
            cardStyle: { backgroundColor: theme.colors.page },
            headerShown: isLoggedIn || isGuestWeb,
          }}
          tabBar={isLoggedIn ? () => null : undefined}
        >
          <Stack.Screen component={IntroScreen} name="onboardingIntro" />
          <Stack.Screen component={PresentPartageClubScreen} name="presentPartageClub" />
          <Stack.Screen
            component={LandingPageScreen}
            name="landing-page"
            options={
              isSmallUI
                ? {
                    headerLeft: MobileHomeHeaderLeftRenderer,
                    headerRight: MobileHomeHeaderRightRenderer,
                  }
                : undefined
            }
          />
          <Stack.Screen
            component={AllListingsScreenLazy}
            name="all-listings"
            options={
              isSmallUI
                ? isGuestWeb
                  ? {
                      header: HeaderRenderer,
                      // The custom guest header has a 48px logo row and a 56px search row.
                      // Set this explicitly so the web stack does not reserve its default, larger header space.
                      headerStyle: { height: GUEST_MOBILE_HEADER_CONTENT_HEIGHT + safeAreaInsets.top } as never,
                    }
                  : {
                      header: HeaderRenderer,
                    }
                : {}
            }
          />
          <Stack.Screen
            component={OneListingScreen}
            name="one-listing"
            options={({ navigation, route }) => {
              if (!isSmallUI) {
                return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
              }
              return {};
            }}
          />
          <Stack.Screen
            component={isGuestWeb ? GuestContactRoute : OneChannelScreen}
            name="one-channel"
            options={({ navigation, route }) => {
              if (!isSmallUI) {
                return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
              }
              return {};
            }}
          />
          <Stack.Screen
            component={isGuestWeb ? GuestContactRoute : LenderParticipateScreen}
            name="listing-activity"
            options={({ navigation, route }) => {
              if (!isSmallUI) {
                return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
              }
              return {};
            }}
          />
          {isFeatureEnabled(FeatureFlag.rateAfterLoans) && (
            <Stack.Screen
              component={isGuestWeb ? GuestReviewsRoute : RatingsScreen}
              name="ratings"
              options={({ navigation, route }) => {
                if (!isSmallUI) {
                  return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
                }
                return {};
              }}
            />
          )}
          <Stack.Screen
            component={GroupedListingScreen}
            name="grouped-listing"
            options={({ navigation, route }) => {
              if (!isSmallUI) {
                return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
              }

              return createModalScreenOptions(navigation, route);
            }}
          />
          <Stack.Screen
            component={isGuestWeb ? GuestProfileRoute : NeighbourProfileScreen}
            name="neighbour-profile"
            options={({ navigation, route }) => {
              if (!isSmallUI) {
                return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
              }

              return {};
            }}
          />

          <Stack.Screen
            component={ProfileSharesScreen}
            name="profile-shares"
            options={({ navigation, route }) =>
              !isSmallUI
                ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>)
                : {}
            }
          />
          <Stack.Screen
            component={TrustedMembersScreen}
            name="trusted-members"
            options={({ navigation, route }) =>
              !isSmallUI
                ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>)
                : {}
            }
          />
        </Stack.Navigator>
      </View>
    </FilterProvider>
  );
}

const Stack2 = createDefaultNativeStackNavigator<Parameters>({
  isModal: true,
  withChat: false,
  withInvite: false,
});
export function ListingNavigator() {
  const { isSmallUI } = useUISize();
  const isGuestWeb = useIsGuestWeb();
  return (
    <Stack2.Navigator initialRouteName="one-listing">
      <Stack2.Screen
        component={OneListingScreen}
        name="one-listing"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }

          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack2.Screen
        component={GroupedListingScreen}
        name="grouped-listing"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack2.Screen
        component={isGuestWeb ? GuestContactRoute : LenderParticipateScreen}
        name="listing-activity"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />

      {isFeatureEnabled(FeatureFlag.rateAfterLoans) && (
        <Stack2.Screen
          component={isGuestWeb ? GuestReviewsRoute : RatingsScreen}
          name="ratings"
          options={({ navigation, route }) => {
            if (!isSmallUI) {
              return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
            }
            return createModalScreenOptions(navigation, route);
          }}
        />
      )}

      <Stack2.Screen
        component={isGuestWeb ? GuestProfileRoute : NeighbourProfileScreen}
        name="neighbour-profile"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }

          return {};
        }}
      />

      <Stack2.Screen
        component={ProfileSharesScreen}
        name="profile-shares"
        options={({ navigation, route }) =>
          !isSmallUI ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>) : {}
        }
      />
      <Stack2.Screen
        component={TrustedMembersScreen}
        name="trusted-members"
        options={({ navigation, route }) =>
          !isSmallUI ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>) : {}
        }
      />
      <Stack2.Screen
        component={isGuestWeb ? GuestProfileRoute : NeighbourListingsScreen}
        name="neighbour-listings"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }

          return {};
        }}
      />

      <Stack2.Screen
        component={isGuestWeb ? GuestContactRoute : OneChannelScreen}
        name="one-channel"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }

          return {};
        }}
      />

      <Stack2.Screen
        component={isGuestWeb ? GuestProposeDatesRoute : LoanDatesPickerScreen}
        name="loan-dates-picker"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack.Navigator>;
          }

          return createModalScreenOptions(navigation, route);
        }}
      />
    </Stack2.Navigator>
  );
}

const Stack3 = createDefaultNativeStackNavigator<Parameters>({
  isModal: true,
  withChat: false,
  withInvite: false,
});

export function UserProfileNavigator() {
  const { isSmallUI } = useUISize();
  const isGuestWeb = useIsGuestWeb();
  return (
    <Stack3.Navigator initialRouteName="neighbour-profile">
      <Stack3.Screen
        component={isGuestWeb ? GuestProfileRoute : NeighbourProfileScreen}
        name="neighbour-profile"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack3.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      <Stack3.Screen
        component={ProfileSharesScreen}
        name="profile-shares"
        options={({ navigation, route }) =>
          !isSmallUI ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack3.Navigator>) : {}
        }
      />
      <Stack3.Screen
        component={TrustedMembersScreen}
        name="trusted-members"
        options={({ navigation, route }) =>
          !isSmallUI ? (createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack3.Navigator>) : {}
        }
      />
      <Stack3.Screen
        component={MyInformationScreen}
        name="my-information"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack3.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
      {isFeatureEnabled(FeatureFlag.rateAfterLoans) && (
        <Stack3.Screen
          component={isGuestWeb ? GuestReviewsRoute : RatingsScreen}
          name="ratings"
          options={({ navigation, route }) => {
            if (!isSmallUI) {
              return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack3.Navigator>;
            }
            return createModalScreenOptions(navigation, route);
          }}
        />
      )}
      <Stack3.Screen
        component={NeighbourListingsScreen}
        name="neighbour-listings"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack3.Navigator>;
          }
          return {};
        }}
      />

      <Stack3.Screen
        component={OneListingScreen}
        name="one-listing"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack3.Navigator>;
          }
          return {};
        }}
      />

      <Stack3.Screen
        component={isGuestWeb ? GuestContactRoute : LenderParticipateScreen}
        name="listing-activity"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack3.Navigator>;
          }
          return {};
        }}
      />
      <Stack3.Screen component={isGuestWeb ? GuestContactRoute : OneChannelScreen} name="one-channel" />
    </Stack3.Navigator>
  );
}

const Stack4 = createDefaultNativeStackNavigator<Parameters>({
  isModal: true,
  withChat: false,
  withInvite: false,
});
export function NewAddressNavigator() {
  const { isSmallUI } = useUISize();
  return (
    <Stack4.Navigator initialRouteName="my-neighbourhood">
      <Stack4.Screen
        component={MyNeighbourhoodScreen}
        name="my-neighbourhood"
        options={({ navigation, route }) => {
          if (!isSmallUI) {
            return createWebModalScreenOptions(navigation, route) as ComponentProps<typeof Stack4.Navigator>;
          }
          return createModalScreenOptions(navigation, route);
        }}
      />
    </Stack4.Navigator>
  );
}
